<template>
  <PageVC figure-hidden class="table-cart">
    <div class="a-container">
      <h1 class="a-page-title">Bordets ordre</h1>
      <HUILoadingView v-if="!order || !menu || !entered" />
      <template v-else-if="sortedOrderLines">
        <!-- <div class="table-cart__description a-rich-text a-rich-text--centered">
          Tekst
        </div>-->
        <div class="a-container__offset">
          <Cart
            :disabled="interactionShouldBeDisabled"
            :order="order"
            :menu="menu"
            :sorted-order-lines="sortedOrderLines"
            :expand-others="order.status === OrderStatus.CLOSED ? false : true"
            @burger-update="handleCartUpdateBurger"
            @burger-delete="handleCartDeleteBurger"
            @product-change="handleCartProductChange"
          ></Cart>
        </div>
        <div class="a-order-buttons">
          <div class="a-order-buttons__inner">
            <div class="a-button-group">
              <RouterButton
                theme="default"
                :to="{ name: routes.TABLE_MENU, params: { orderToken: $route.params.orderToken }}"
              >Se menu</RouterButton>
              <HUIButton
                v-if="numUnsentItems > 0"
                :disabled="interactionShouldBeDisabled"
                :spinner="sending"
                theme="green"
                @click="handleSendToKitchenClick"
              >Bestil</HUIButton>
              <HUIButton
                v-if="numUnsentItems === 0 && paidAmount !== totalAmount"
                :disabled="interactionShouldBeDisabled"
                :spinner="paying"
                theme="red"
                @click="handleCheckoutClick"
              >Betal regning</HUIButton>
            </div>
          </div>
        </div>
      </template>
      <template v-else>
        <div
          class="table-cart__empty-text a-rich-text a-rich-text--centered"
        >Der er ikke blevet bestilt noget til bordet endnu</div>
        <RouterButton
          theme="green"
          :to="{ name: routes.TABLE_MENU, params: { orderToken: $route.params.orderToken }}"
        >Bestil mad</RouterButton>
      </template>
    </div>
    <ModalVC
      ref="payment-window"
      v-if="paymentLink"
      color="light-brown"
      heading="Betaling"
      @close="handlePaymentModalClosed"
    >
      <PaymentWindow
        :payment-link="paymentLink"
        :order-token="orderToken"
        @aborted="handlePaymentAborted"
        @complete="handlePaymentComplete"
        @failed="handlePaymentFailed"
      ></PaymentWindow>
    </ModalVC>
  </PageVC>
</template>

<script>
import { HUILoadingView, HUIButton, HUIRating } from '@/halifax-ui'
import TableBase from '@/views/TableBase'
import PageVC from '@/containers/PageVC'
import ModalVC from '@/containers/ModalVC'
import PaymentWindow from '@/containers/PaymentWindow'
import Cart from '@/containers/Cart'
import RouterButton from '@/containers/RouterButton'
import sortsOrderLinesOnChange from '@/mixins/sortsOrderLinesOnChange'
import routes from '@/router/routes'
import { OrderStatus } from '@/store/order'

export default {
  extends: TableBase,
  mixins: [sortsOrderLinesOnChange],
  components: {
    PageVC,
    ModalVC,
    Cart,
    HUILoadingView,
    HUIButton,
    PaymentWindow,
    RouterButton
  },
  data() {
    return {
      paying: false,
      sending: false,
      paymentLink: null,
      routes,
      OrderStatus
    }
  },
  computed: {
    interactionShouldBeDisabled() {
      return this.sending || this.paying || this.order.is_locked
    }
  },
  beforeDestroy() {
    // If user tries to leave with the paymentwindow active, unlock
    if (this.paymentLink) {
      this.$store.dispatch('order/UNLOCK', this.orderToken)
    }
  },
  methods: {
    async handleCartDeleteBurger(config) {
      try {
        await this.$store.dispatch('order/REMOVE_ITEM', {
          orderLineToken: config.orderLineToken,
          orderToken: this.orderToken
        })
      } catch (error) {
        this.$store.dispatch('toast/ERROR', error.message)
      }
    },
    async handleCartUpdateBurger(config) {
      const payload = {
        type: 'burger',
        burger_id: config.id,
        bun_id: config.bun,
        patty_id: config.patty,
        frying_id: config.frying,
        ingredients: config.ingredients,
        quantity: config.quantity
      }
      try {
        await this.$store.dispatch('order/UPDATE_ITEM', {
          payload,
          orderLineToken: config.orderLineToken,
          orderToken: this.orderToken
        })
      } catch (error) {
        this.$store.dispatch('toast/ERROR', error.message)
      }
    },
    async handleCartProductChange({ productId, quantity, orderLineToken }) {
      if (quantity === 0) {
        try {
          await this.$store.dispatch('order/REMOVE_ITEM', {
            orderToken: this.orderToken,
            orderLineToken
          })
        } catch (error) {
          this.$store.dispatch('toast/ERROR', error.message)
        }
      } else {
        const payload = {
          type: 'product',
          product_id: productId,
          quantity
        }
        try {
          await this.$store.dispatch('order/UPDATE_ITEM', {
            payload,
            orderToken: this.orderToken,
            orderLineToken
          })
        } catch (error) {
          this.$store.dispatch('toast/ERROR', error.message)
        }
      }
    },
    async handlePaymentFailed(error) {
      await this.$refs['payment-window'].close()
      this.fetchOrder()

      setTimeout(() => {
        this.$store.dispatch('toast/ERROR', error.message)
      }, 400)
    },
    async handlePaymentAborted() {
      await this.$refs['payment-window'].close()
      this.fetchOrder()

      setTimeout(() => {
        this.$store.dispatch('toast/ERROR', 'Betalingen blev afbrudt.')
      }, 400)
    },
    async handlePaymentComplete() {
      await this.$refs['payment-window'].close()
      this.fetchOrder()

      setTimeout(() => {
        this.$store.dispatch('toast/SUCCESS', 'Betalingen blev gennemført.')
      }, 400)
    },
    handlePaymentModalClosed() {
      this.$store.dispatch('order/UNLOCK', this.orderToken)
      this.paymentLink = null
    },
    async handleCheckoutClick() {
      this.paying = true
      const canChangeOrder = await this.canChangeOrder()

      if (canChangeOrder) {
        this.showPaymentModal()
      } else {
        this.paying = false
      }
    },
    async showPaymentModal() {
      try {
        const [, paymentLink] = await Promise.all([
          this.$store.dispatch('order/LOCK', this.orderToken),
          this.$store.dispatch('order/CREATE_PAYMENT_LINK', {
            orderToken: this.orderToken,
            price: this.totalAmount - this.paidAmount
          })
        ])
        this.paymentLink = paymentLink
      } catch (error) {
        this.$store.dispatch('toast/ERROR', error.message)
      } finally {
        this.paying = false
      }
    },
    async handleSendToKitchenClick() {
      this.sending = true
      const canChangeOrder = await this.canChangeOrder()

      if (canChangeOrder) {
        try {
          const didConfirm = await this.$store.dispatch('dialog/CONFIRM', {
            props: {
              heading: 'Er alle færdige?',
              body: 'Når du trykker ok, sendes hele bordets bestilling af sted.'
            }
          })
          if (didConfirm) {
            await this.$store.dispatch('order/SEND_TO_KITCHEN', this.orderToken)
            await this.fetchOrder()

            this.$store.dispatch('dialog/ALERT', {
              props: {
                heading: 'Tak for din bestilling.',
                body: 'Vi glæder os til at servere den for dig.'
              }
            })
          }
        } catch (error) {
          this.$store.dispatch('toast/ERROR', error.message)
        } finally {
          this.sending = false
        }
      } else {
        this.sending = false
      }
    }
  }
}
</script>

<style lang="scss">
@import '~@/scss/variables';
@import '~@/halifax-ui/src/scss/framework';

.table-cart {
  $module: '.table-cart';

  padding-bottom: 160px;

  @include supports-safe-area-insets {
    padding-bottom: calc(var(--safe-area-inset-bottom) + 160px);
  }
  &__description {
    margin-bottom: 40px;
    margin-top: -30px;
  }
  &__empty-text {
    margin-bottom: 50px;
  }
  &__rate {
    margin-top: 40px;
  }
}
</style>
